import { useState } from "react";
import Card from "@rio-cloud/rio-uikit/Card";
import ContentLoader from "@rio-cloud/rio-uikit/ContentLoader";
import useWindowResize from "@rio-cloud/rio-uikit/useWindowResize";
import { SCREEN_SM } from "@rio-cloud/rio-uikit/utils/deviceUtils";
import { getIndentStyle, DRE_TOGGLE_COLUMN_STYLE, DRE_ITEM_LABEL_STYLE, type DREIndent } from "../DRETable/DRERow";

const INDENT_WIDE: DREIndent = { base: 25, step: 25, maxLevel: Number.MAX_SAFE_INTEGER };
const INDENT_COMPACT: DREIndent = { base: 10, step: 10, maxLevel: 3 };

const LABEL_WIDTHS = ["62%", "84%", "52%", "73%", "45%", "90%", "58%", "68%", "76%", "48%", "66%", "86%"];
const SECOND_LINE_WIDTHS = ["38%", "56%", "30%", "44%", "26%", "62%", "34%", "40%", "50%", "28%", "42%", "58%"];

interface DRETableSkeletonProps {
  rows?: number;
}

export default function DRETableSkeleton({ rows = 8 }: Readonly<DRETableSkeletonProps>) {
  const [isCompact, setIsCompact] = useState(() => window.innerWidth < SCREEN_SM);

  useWindowResize(() => setIsCompact(window.innerWidth < SCREEN_SM));

  const indent = isCompact ? INDENT_COMPACT : INDENT_WIDE;

  const rowItems = Array.from({ length: rows }, (_, index) => ({
    key: `dre-table-skeleton-row-${index}`,
    isLast: index === rows - 1,
    labelWidth: LABEL_WIDTHS[index % LABEL_WIDTHS.length],
    secondLineWidth: SECOND_LINE_WIDTHS[index % SECOND_LINE_WIDTHS.length],
  }));

  return (
    <Card className="padding-y-20 padding-x-15 padding-x-50-md margin-bottom-20">
      <div className="display-flex align-items-center justify-content-between gap-10 padding-bottom-20">
        <span className="text-size-16">
          <ContentLoader height={16} width={260} />
        </span>
        <span>
          <ContentLoader height={30} width={30} />
        </span>
      </div>

      <div className="overflow-x-auto">
        <div className="min-width-500">
          <div className="display-flex align-items-center justify-content-between gap-10 padding-y-10 text-color-gray text-size-12 text-size-14-sm">
            <div
              className="flex-1-1 min-width-0 display-flex align-items-center gap-5"
              style={getIndentStyle(0, indent)}
            >
              <span style={DRE_TOGGLE_COLUMN_STYLE} />
              <span>
                <ContentLoader height={14} width={60} />
              </span>
            </div>
            <div className="flex-0 width-100 width-150-sm text-right">
              <ContentLoader height={14} width={70} />
            </div>
            <div className="flex-0 width-100 width-150-sm text-right">
              <ContentLoader height={14} width={70} />
            </div>
            <div className="flex-0 width-100 width-150-sm text-right">
              <ContentLoader height={14} width={95} />
            </div>
          </div>

          {rowItems.map((row) => (
            <div
              key={row.key}
              className={`display-flex align-items-center justify-content-between gap-10 padding-y-10 border ${
                row.isLast ? "border-none" : "border-bottom-only"
              }`}
            >
              <div
                className="flex-1-1 min-width-0 display-flex align-items-center gap-5"
                style={getIndentStyle(0, indent)}
              >
                <span style={DRE_TOGGLE_COLUMN_STYLE} />
                <span className="flex-1-1 display-flex flex-column gap-2" style={DRE_ITEM_LABEL_STYLE}>
                  <ContentLoader height={14} width={row.labelWidth} />
                  {isCompact && <ContentLoader height={14} width={row.secondLineWidth} />}
                </span>
              </div>

              <div className="flex-0 width-100 width-150-sm text-right">
                <ContentLoader height={14} width={90} />
              </div>

              <div className="flex-0 width-100 width-150-sm text-right">
                <ContentLoader height={14} width={90} />
              </div>

              <div className="flex-0 width-100 width-150-sm text-right">
                <ContentLoader height={14} width={90} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}
